import React, { useState } from 'react';
import {
  Box,
  DefaultCellContent,
  Flex,
  IconButton,
  Modal,
  Table,
  TableColumnType,
  Text,
  useSorting,
} from '@fullstory/components';
import { PageFlowEdge } from '@fullstory/proto/fs/api/metrics/pagemetrics';
import { FormattedMessage, useIntl } from 'react-intl';
import { int64 } from '@fullstory/proto/google/protobuf';
import { orderBy } from 'lodash';
import { PageTypes } from '../components/PageFlow/types';
import { PAGE_FLOW_TOP_PAGES_COUNT } from '../constants';

const ROWS_PER_PAGE = 5;

interface UseOtherPagesModalConfig {
  entryOther: PageFlowEdge[];
  exitOther: PageFlowEdge[];
  getPageNameById: (pageDefId: number) => string;
  pageName: string;
  incomingCount: number;
  incomingEdgeCount: number;
  outgoingCount: number;
  outgoingEdgeCount: number;
  onClose: () => void;
}

export const useOtherPagesModal = ({
  entryOther,
  exitOther,
  getPageNameById,
  pageName,
  incomingCount,
  incomingEdgeCount,
  outgoingCount,
  outgoingEdgeCount,
  onClose,
}: UseOtherPagesModalConfig) => {
  const intl = useIntl();
  const [entryOrExit, setEntryOrExit] = useState<PageTypes | null>(null);
  const [page, setPage] = useState(1);
  const isEntry = entryOrExit === PageTypes.ENTRY;
  const isExit = entryOrExit === PageTypes.EXIT;

  const events = (isEntry && entryOther) || (isExit && exitOther) || [];
  const numEvents = events.length;
  const numPages = Math.ceil(numEvents / ROWS_PER_PAGE);
  const pageForward = () => page < numPages && setPage(page + 1);
  const pageBack = () => page > 1 && setPage(page - 1);

  const sortingProps = useSorting<PageFlowEdge>(events, 'pageviews', {
    defaultSortDirection: 'desc',
    sortFunction: (entities, _, direction) =>
      orderBy(entities, ({ count }) => int64.toNumber(count), direction),
  });
  const rowStartIdx = (page - 1) * ROWS_PER_PAGE;
  const rowEndIdx = page < numPages ? page * ROWS_PER_PAGE : undefined;
  const rows = sortingProps.rows.slice(rowStartIdx, rowEndIdx);

  let totalOtherEvents = 0;
  if (isEntry) {
    totalOtherEvents = incomingEdgeCount - PAGE_FLOW_TOP_PAGES_COUNT;
  } else if (isExit) {
    totalOtherEvents = outgoingEdgeCount - PAGE_FLOW_TOP_PAGES_COUNT;
  }

  const columns: TableColumnType<PageFlowEdge>[] = [
    {
      key: 'eventLabel',
      label:
        totalOtherEvents > events.length
          ? intl.formatMessage(
              {
                defaultMessage:
                  'Showing top {numEvents, number} out of {totalOtherEvents, number} pages',
                id: 'vOTIfT',
              },
              { numEvents, totalOtherEvents },
            )
          : intl.formatMessage(
              { defaultMessage: 'Showing top {numEvents, number} pages', id: 'EhJLJJ' },
              { numEvents },
            ),
      renderCell: (event: PageFlowEdge) => (
        <DefaultCellContent label={getPageNameById(event.pageDefId)} />
      ),
      width: 320,
    },
    {
      key: 'pageviewsPercent',
      label: intl.formatMessage({ defaultMessage: '% Pageviews', id: 'J9ayRS' }),
      renderCell: (event: PageFlowEdge) => {
        const count = int64.toNumber(event.count);
        const percentBasis = isEntry ? incomingCount : outgoingCount;
        const countPercent = count / percentBasis; // TODO
        const label = intl.formatMessage(
          {
            defaultMessage: '{countPercent, number, ::percent .0}',
            id: '8kPb9x',
          },
          { countPercent },
        );
        return <DefaultCellContent label={label} />;
      },
      width: 200,
    },
    {
      key: 'pageviews',
      label: intl.formatMessage({ defaultMessage: 'Pageviews', id: '0UAnct' }),
      renderCell: (event: PageFlowEdge) => {
        const count = int64.toNumber(event.count);
        const label = intl.formatMessage(
          {
            defaultMessage: '{count, number, ::compact-short}',
            id: 'u9P/9g',
          },
          { count },
        );
        return <DefaultCellContent label={label} />;
      },
      sortable: true,
    },
  ];

  const onCloseSignal = () => {
    setPage(1);
    setEntryOrExit(null);
    onClose();
  };

  const ModalComponent =
    entryOrExit && numEvents ? (
      <Modal onCloseSignal={onCloseSignal}>
        <Modal.Header onCloseSignal={onCloseSignal}>{pageName}</Modal.Header>
        <Modal.Body>
          <Box margin={[16, 8, 8, 8]} minHeight={360}>
            <Box margin={[0, 0, 16, 24]}>
              <Text appearance="bodyLarge">
                {entryOrExit === PageTypes.ENTRY ? (
                  <FormattedMessage defaultMessage="Previous Page" id="6TbqAD" />
                ) : (
                  <FormattedMessage defaultMessage="Next Page" id="uuLnK5" />
                )}
              </Text>
            </Box>
            <Table
              columns={columns}
              rowKeyAccessor="pageDefId"
              headProps={{
                variant: 'dark',
              }}
              {...sortingProps}
              rows={rows}
            />
          </Box>
          {numPages > 1 && (
            <Flex>
              <Box width="50%">
                <Flex justifyContent="space-between">
                  <IconButton
                    iconName="chevron-left"
                    title={intl.formatMessage({ defaultMessage: 'Previous page', id: 'k9hDFZ' })}
                    disabled={page === 1}
                    onClick={pageBack}
                  />
                  <Text appearance="bodySmall">
                    <FormattedMessage
                      defaultMessage="{page} of {numPages}"
                      values={{ page, numPages }}
                      id="n5yLih"
                    />
                  </Text>
                  <IconButton
                    iconName="chevron-right"
                    title={intl.formatMessage({ defaultMessage: 'Next page', id: 'rBj9Ib' })}
                    disabled={page === numPages}
                    onClick={pageForward}
                  />
                </Flex>
              </Box>
            </Flex>
          )}
        </Modal.Body>
      </Modal>
    ) : null;
  return {
    setEntryOrExit,
    ModalComponent,
  };
};
